import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Video, Mic, MicOff, VideoOff, PhoneOff, Music, 
  User, Mail, Clock, Calendar, FileText, Drum,
  CheckCircle, AlertCircle, Loader2, Maximize2, Minimize2
} from 'lucide-react';
import { toast } from 'sonner';
import AdminFreeAccessBar, { generateStudioRoomCode } from '../components/admin/AdminFreeAccessBar';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Instrument options
const INSTRUMENTS = [
  'Drums/Percussion', 'Guitar', 'Bass', 'Vocals', 'Piano/Keys',
  'Strings', 'Brass', 'Woodwinds', 'Electronic', 'Other'
];

const DURATION_OPTIONS = [
  { value: 1, label: '1 Hour' },
  { value: 2, label: '2 Hours' },
  { value: 3, label: '3 Hours' },
  { value: 4, label: '4+ Hours' },
];

const EXPERIENCE_LEVELS = [
  { value: 'beginner', label: 'Beginner - New to recording' },
  { value: 'intermediate', label: 'Intermediate - Some studio experience' },
  { value: 'advanced', label: 'Advanced - Professional musician' },
];

const JoinSession = () => {
  const { sessionCode } = useParams();
  const [searchParams] = useSearchParams();
  
  // Session state
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: Form, 2: Payment (if required), 3: Video Room
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    expectations: '',
    trackCount: 1,
    preferredDate: '',
    preferredTime: '',
    duration: 1,
    instruments: [],
    experienceLevel: 'intermediate',
    additionalNotes: ''
  });
  
  // Video room state
  const [inSession, setInSession] = useState(false);
  const [micEnabled, setMicEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Video refs
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const wsRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  
  // Fetch session details
  useEffect(() => {
    fetchSession();
  }, [sessionCode]);
  
  // Check if returning from payment
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const submissionId = searchParams.get('submission');
    
    if (paymentStatus === 'success' && submissionId) {
      // Payment successful, go to video room
      confirmPaymentAndJoin(submissionId);
    } else if (paymentStatus === 'cancelled') {
      toast.info('Payment was cancelled. You can try again.');
    }
  }, [searchParams]);
  
  const fetchSession = async () => {
    try {
      const res = await fetch(`${API}/api/live-studio/sessions/${sessionCode}`);
      if (!res.ok) {
        if (res.status === 404) {
          setError('Session not found. Please check your invite link.');
        } else {
          setError('Unable to load session. Please try again.');
        }
        return;
      }
      const data = await res.json();
      setSession(data.session);
      
      // If session is free and already submitted, go to video room
      if (data.session.client_submitted && !data.session.requires_payment) {
        setStep(3);
        initializeVideoRoom(data.session.room_id);
      }
    } catch (err) {
      setError('Unable to connect. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  };
  
  const confirmPaymentAndJoin = async (submissionId) => {
    try {
      const res = await fetch(`${API}/api/live-studio/submissions/${submissionId}/confirm`, {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success('Payment confirmed! Redirecting to video room...');
        // Redirect to unified video room
        window.location.href = `/video-room/${data.room_id || sessionCode}`;
      }
    } catch (err) {
      toast.error('Error confirming payment. Please contact support.');
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleInstrumentToggle = (instrument) => {
    setFormData(prev => ({
      ...prev,
      instruments: prev.instruments.includes(instrument)
        ? prev.instruments.filter(i => i !== instrument)
        : [...prev.instruments, instrument]
    }));
  };
  
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.expectations) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    
    try {
      const res = await fetch(`${API}/api/live-studio/sessions/${sessionCode}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Submission failed');
      }
      
      if (data.requires_payment && data.checkout_url) {
        // Redirect to Stripe
        window.location.href = data.checkout_url;
      } else {
        // Free session - redirect to unified video room
        toast.success('Form submitted! Redirecting to video room...');
        window.location.href = `/video-room/${data.room_id || sessionCode}`;
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const initializeVideoRoom = async (roomId) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      localStreamRef.current = stream;
      
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' }
        ]
      });
      
      peerConnectionRef.current = pc;
      
      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
      });
      
      pc.ontrack = (event) => {
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = event.streams[0];
        }
        toast.success('Mestre connected!');
      };
      
      pc.onicecandidate = (event) => {
        if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'ice-candidate',
            candidate: event.candidate
          }));
        }
      };
      
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = API.replace('https://', '').replace('http://', '');
      console.log('Client connecting to room:', roomId, 'via', `${wsProtocol}//${wsHost}/api/live-studio/ws/${roomId}/client`);
      const ws = new WebSocket(`${wsProtocol}//${wsHost}/api/live-studio/ws/${roomId}/client`);
      
      wsRef.current = ws;
      
      ws.onopen = () => {
        setInSession(true);
        toast.success('Connected to studio!');
      };
      
      ws.onmessage = async (event) => {
        const message = JSON.parse(event.data);
        console.log('Client received:', message.type);
        
        // CLIENT: Don't create offers, only respond to offers from HOST
        if (message.type === 'peer_joined') {
          console.log('Host joined - waiting for offer');
          // Don't create offer - wait for host to send one
        }
        
        if (message.type === 'answer') {
          // This shouldn't happen for client, but handle gracefully
          console.log('Client received answer (unexpected)');
        }
        
        if (message.type === 'offer') {
          console.log('Client received offer from host');
          await pc.setRemoteDescription(new RTCSessionDescription({
            type: 'offer', sdp: message.sdp
          }));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          ws.send(JSON.stringify({ type: 'answer', sdp: answer.sdp }));
          console.log('Client sent answer');
        }
        
        if (message.type === 'ice-candidate' && message.candidate) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(message.candidate));
          } catch (e) {
            console.error('Error adding ICE candidate:', e);
          }
        }
        
        if (message.type === 'peer_left') {
          toast.info('Mestre disconnected');
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = null;
          }
        }
      };
      
      ws.onerror = () => toast.error('Connection error');
      
    } catch (err) {
      toast.error('Could not access camera/microphone. Please check permissions.');
    }
  };
  
  const toggleMic = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getAudioTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setMicEnabled(track.enabled);
      }
    }
  };
  
  const toggleVideo = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getVideoTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setVideoEnabled(track.enabled);
      }
    }
  };
  
  const endSession = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (peerConnectionRef.current) peerConnectionRef.current.close();
    if (wsRef.current) wsRef.current.close();
    
    if (localVideoRef.current) localVideoRef.current.srcObject = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    
    setInSession(false);
    toast.info('Session ended');
  };
  
  // Loading state
  if (loading && !session) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="bg-[#141414] rounded-2xl p-8 text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Session Not Found</h2>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }
  
  // Video Room (Step 3)
  if (step === 3) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white">
        <div className="max-w-6xl mx-auto p-4">
          {/* Video Area */}
          <div className="relative aspect-video bg-black rounded-2xl overflow-hidden">
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
            
            {!remoteVideoRef.current?.srcObject && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 to-black">
                <div className="w-32 h-32 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center mb-4">
                  <Music className="w-16 h-16 text-black" />
                </div>
                <h3 className="text-xl font-bold">Mestre Curtis Pierre</h3>
                <p className="text-gray-400">Waiting to connect...</p>
              </div>
            )}
            
            <div className="absolute bottom-4 right-4 w-48 aspect-video rounded-xl overflow-hidden border-2 border-green-500">
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
            </div>
            
            <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg px-4 py-2 flex items-center gap-3">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">LIVE SESSION</span>
            </div>
          </div>
          
          {/* Controls */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={toggleMic}
              className={`w-14 h-14 rounded-full flex items-center justify-center ${
                micEnabled ? 'bg-gray-800 hover:bg-gray-700' : 'bg-red-500'
              }`}
            >
              {micEnabled ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
            </button>
            
            <button
              onClick={toggleVideo}
              className={`w-14 h-14 rounded-full flex items-center justify-center ${
                videoEnabled ? 'bg-gray-800 hover:bg-gray-700' : 'bg-red-500'
              }`}
            >
              {videoEnabled ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
            </button>
            
            <button
              onClick={endSession}
              className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center"
            >
              <PhoneOff className="w-6 h-6" />
            </button>
          </div>
          
          <p className="text-center text-gray-500 text-sm mt-4">
            {inSession ? 'Connected • Recording available after session' : 'Connecting...'}
          </p>
        </div>
      </div>
    );
  }
  
  // Client Intake Form (Step 1)
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <AdminFreeAccessBar
        title="Admin — Guest Join Page · Free Access"
        subtitle="Skip the guest form. Jump straight into a fresh live room as host."
        testid="admin-bar-join"
        primary={{
          label: 'Enter as Host Instead',
          icon: Video,
          onClick: () => {
            const code = sessionCode || generateStudioRoomCode();
            window.location.href = `/video-room/${code}/host`;
          },
        }}
        secondary={{
          label: 'Open DAW',
          onClick: () => { window.location.href = '/admin/studio-session'; },
        }}
      />
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            Join Live Studio Session
          </h1>
          <p className="text-green-100">
            with Mestre Curtis Pierre
          </p>
          {session?.requires_payment && session?.price && (
            <p className="mt-2 text-xl font-bold">
              Session Fee: ${session.price}
            </p>
          )}
          {!session?.requires_payment && (
            <p className="mt-2 text-green-200 text-sm">
              ✓ Free Session - No payment required
            </p>
          )}
        </div>
      </div>
      
      {/* Form */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmitForm} className="space-y-6">
          
          {/* Personal Info */}
          <div className="bg-[#141414] rounded-2xl p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-green-500" />
              Your Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-[#0a0a0a] border border-gray-800 rounded-xl focus:border-green-500 focus:outline-none"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-[#0a0a0a] border border-gray-800 rounded-xl focus:border-green-500 focus:outline-none"
                  placeholder="john@example.com"
                />
              </div>
            </div>
          </div>
          
          {/* Session Details */}
          <div className="bg-[#141414] rounded-2xl p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-500" />
              Session Details
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  What are you expecting from this session? *
                </label>
                <textarea
                  name="expectations"
                  value={formData.expectations}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  className="w-full px-4 py-3 bg-[#0a0a0a] border border-gray-800 rounded-xl focus:border-green-500 focus:outline-none resize-none"
                  placeholder="Describe what you'd like to accomplish in this session..."
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    How many tracks/songs?
                  </label>
                  <input
                    type="number"
                    name="trackCount"
                    value={formData.trackCount}
                    onChange={handleInputChange}
                    min="1"
                    max="20"
                    className="w-full px-4 py-3 bg-[#0a0a0a] border border-gray-800 rounded-xl focus:border-green-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Expected Duration
                  </label>
                  <select
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-[#0a0a0a] border border-gray-800 rounded-xl focus:border-green-500 focus:outline-none"
                  >
                    {DURATION_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          
          {/* Schedule Preferences */}
          <div className="bg-[#141414] rounded-2xl p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-green-500" />
              Schedule Preferences
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Preferred Date</label>
                <input
                  type="date"
                  name="preferredDate"
                  value={formData.preferredDate}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-[#0a0a0a] border border-gray-800 rounded-xl focus:border-green-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Preferred Time</label>
                <input
                  type="time"
                  name="preferredTime"
                  value={formData.preferredTime}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-[#0a0a0a] border border-gray-800 rounded-xl focus:border-green-500 focus:outline-none"
                />
              </div>
            </div>
          </div>
          
          {/* Instruments */}
          <div className="bg-[#141414] rounded-2xl p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Drum className="w-5 h-5 text-green-500" />
              Instruments You'll Be Recording
            </h2>
            
            <div className="flex flex-wrap gap-2">
              {INSTRUMENTS.map(instrument => (
                <button
                  key={instrument}
                  type="button"
                  onClick={() => handleInstrumentToggle(instrument)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    formData.instruments.includes(instrument)
                      ? 'bg-green-500 text-black'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  {instrument}
                </button>
              ))}
            </div>
          </div>
          
          {/* Experience Level */}
          <div className="bg-[#141414] rounded-2xl p-6">
            <h2 className="text-xl font-bold mb-4">Your Experience Level</h2>
            
            <div className="space-y-2">
              {EXPERIENCE_LEVELS.map(level => (
                <label
                  key={level.value}
                  className={`flex items-center p-4 rounded-xl cursor-pointer transition-all ${
                    formData.experienceLevel === level.value
                      ? 'bg-green-500/20 border border-green-500'
                      : 'bg-[#0a0a0a] border border-gray-800 hover:border-gray-700'
                  }`}
                >
                  <input
                    type="radio"
                    name="experienceLevel"
                    value={level.value}
                    checked={formData.experienceLevel === level.value}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                    formData.experienceLevel === level.value
                      ? 'border-green-500 bg-green-500'
                      : 'border-gray-600'
                  }`}>
                    {formData.experienceLevel === level.value && (
                      <div className="w-2 h-2 rounded-full bg-black" />
                    )}
                  </div>
                  <span>{level.label}</span>
                </label>
              ))}
            </div>
          </div>
          
          {/* Additional Notes */}
          <div className="bg-[#141414] rounded-2xl p-6">
            <h2 className="text-xl font-bold mb-4">Additional Notes</h2>
            <textarea
              name="additionalNotes"
              value={formData.additionalNotes}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-4 py-3 bg-[#0a0a0a] border border-gray-800 rounded-xl focus:border-green-500 focus:outline-none resize-none"
              placeholder="Any other information you'd like to share..."
            />
          </div>
          
          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 disabled:from-gray-700 disabled:to-gray-700 text-black font-bold rounded-xl flex items-center justify-center gap-2"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : session?.requires_payment ? (
              <>Continue to Payment - ${session.price}</>
            ) : (
              <>Join Session</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default JoinSession;
